import { sidebarOpenTabAtom } from '@/atoms/persistedPreferencesAtoms';
import {
  sessionlessEvidenceAwarenessDevForceShowAtom,
  sessionlessEvidenceAwarenessDevVariantOverrideAtom,
} from '@/atoms/sessionlessEvidenceAwarenessDevAtoms';
import {
  GET_EVIDENCE_AWARENESS_POPUP_VARIANT,
  IS_SIDEBAR_PANEL_ENABLED,
} from '@/components/constants/flags';
import { UploadSessionScheduleModal } from '@/components/layout/lazy-session-dialogs';
import { SessionsListAssistantView } from '@/components/layout/Navigation/sessions/side-nav-list/components/sessions-list-assistant-view';
import { SessionsListLoading } from '@/components/layout/Navigation/sessions/side-nav-list/components/sessions-list-loading';
import EhrSessionsListLoader from '@/components/layout/Navigation/sessions/side-nav-list/integration/ehr-sessions-list/ehr-sessions-list-loader';
import { useLinkEhrPatientToSession } from '@/components/layout/Navigation/sessions/side-nav-list/integration/hooks/useLinkEhrPatientToSession';
import { SessionlessEvidenceAwarenessPopover } from '@/components/modal/sessionless-evidence-awareness-popover';
import { useShowSessionsListAssistantView } from '@/composition/hosts/session/session-list/useShowSessionsListAssistantView';
import { ConsolidatedSessionListItem } from '@/features/consolidatedSessionsList/components/consolidatedSessionListItem';
import SessionListHeader from '@/features/consolidatedSessionsList/components/sessionListHeader/sessionListHeader';
import SessionSelectionBar from '@/features/consolidatedSessionsList/components/sessionSelectionBar';
import { useConsolidatedSessionsList } from '@/features/consolidatedSessionsList/hooks/useConsolidatedSessionsList';
import { useSessionSelectionCleanup } from '@/features/consolidatedSessionsList/hooks/useSessionSelectionCleanup';
import {
  isLegacyEhrAppointment,
  type ConsolidatedSession,
} from '@/features/consolidatedSessionsList/types';
import { SessionCreationInProgressBanner } from '@/features/sessions/components/session-creation-in-progress-banner';
import { PERMISSION_SUBJECTS } from '@/helpers/casl/ability';
import { useMutateStatePostIntegrationWorkflowSession } from '@/hooks/mutation/integration/direct-backend/useMutatePostIntegrationWorkflowSession';
import { useQueryCombinedSessions } from '@/hooks/query/useQueryCombinedSessions';
import { useRBACPermissionCheck } from '@/hooks/useRBACPermissionCheck';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import { TypographyP2 } from '@heidi/design-system/typography';
import { startTracking, TrackingKey } from '@heidi/runtime/performance';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import {
  isSessionSearchBarOpenAtom,
  isUpcomingSessionsListLoadingAtom,
  isUploadSessionScheduleModalOpenAtom,
  scheduledSessionsDraftAtom,
  showPreviewAtom,
} from '@heidi/session';
import { getTimezone } from '@heidi/utils/get-country';
import { useAtomValue } from 'jotai';
import { CalendarX } from 'lucide-react';
import { useRouter } from 'next/router';
import { memo, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { consolidatedSessionsListMessages } from '../consolidatedSessionsList.messages';

const ConsolidatedSessionsBar = memo(() => {
  const router = useRouter();
  const isMobile = useIsMobile();
  const currentSessionId = useCurrentSessionId();
  const isSessionSearchBarOpen = useAtomValue(isSessionSearchBarOpenAtom);
  useAtomValue(sessionlessEvidenceAwarenessDevVariantOverrideAtom);
  useAtomValue(sessionlessEvidenceAwarenessDevForceShowAtom);
  const isUploadSessionScheduleModalOpen = useAtomValue(
    isUploadSessionScheduleModalOpenAtom
  );
  const showUploadPreview = useAtomValue(showPreviewAtom);
  const draftSessions = useAtomValue(scheduledSessionsDraftAtom);
  const isUploadSessionProcessing = useAtomValue(isUpcomingSessionsListLoadingAtom);
  const { isLoading, isFetchingNextPage } = useQueryCombinedSessions();
  const showSessionsListAssistantView = useShowSessionsListAssistantView();

  const sidebarOpenTab = useAtomValue(sidebarOpenTabAtom);
  // When the sidebar panel is enabled, it manages its own visibility —
  // the Redux sidebarOpenTab is never set, so always treat the list as open.
  const isSidebarSessionListOpen = IS_SIDEBAR_PANEL_ENABLED() || sidebarOpenTab;
  const { checkRBACPermission } = useRBACPermissionCheck();
  const canCreateSessions = useMemo(() => {
    return checkRBACPermission(
      PERMISSION_SUBJECTS.TEAM_OTHERS_SESSION_CREATE,
      PERMISSION_SUBJECTS.SESSION_CREATE
    );
  }, [checkRBACPermission]);

  // Consolidated sessions data
  const { consolidatedSessions, isPending: isConsolidatedPending } =
    useConsolidatedSessionsList();

  // Prune stale atomFamily entries when the visible session list changes
  useSessionSelectionCleanup(consolidatedSessions);

  // EHR session linking handlers
  const { isPending: isLinkEhrPatientToSessionPending, onLinkEhrPatientToSession } =
    useLinkEhrPatientToSession();
  const {
    mutateAsync: postIntegrationWorkflowSession,
    isPending: isPostIntegrationWorkflowSessionPending,
  } = useMutateStatePostIntegrationWorkflowSession();

  const isEhrActionPending =
    isLinkEhrPatientToSessionPending || isPostIntegrationWorkflowSessionPending;

  const height = useMemo(() => {
    return !isSessionSearchBarOpen ? 'calc(100% - 32px)' : 'calc(100% - 62px)';
  }, [isSessionSearchBarOpen]);

  const showSessionsListLoading = isLoading && !isFetchingNextPage;
  const isUploadParsing =
    isUploadSessionScheduleModalOpen && showUploadPreview && draftSessions.length === 0;
  const showUploadProcessingOverlay = isUploadParsing || isUploadSessionProcessing;

  const showSidebarEvidenceAwareness =
    GET_EVIDENCE_AWARENESS_POPUP_VARIANT() === 'sidebar';

  const showNoSessions = useMemo(() => {
    return (
      !showSessionsListLoading &&
      !isConsolidatedPending &&
      consolidatedSessions &&
      !consolidatedSessions.length
    );
  }, [consolidatedSessions, showSessionsListLoading, isConsolidatedPending]);

  // Handle session click - navigate to existing session or create from EHR appointment
  const handleSessionClick = useCallback(
    (session: ConsolidatedSession) => {
      if (isEhrActionPending || session.session_id === currentSessionId) return;
      if (session.is_offline_synthetic) return;

      if (session.isFromEhr && session?.externalApptData && !session.session_id) {
        // EHR appointment without linked session - create new session
        if (isLegacyEhrAppointment(session.externalApptData)) {
          void onLinkEhrPatientToSession(session.externalApptData.data);
        } else {
          // Integration schedule item
          void postIntegrationWorkflowSession({
            session: { is_auto_mode: false, timezone: getTimezone() },
            appointment: session.externalApptData.data.appointment,
            patient: session.externalApptData.data.patient,
          });
        }
      } else if (session.session_id) {
        // Navigate to existing session
        startTracking({
          key: TrackingKey.SCRIBE_SESSION_OPEN,
          metadata: { session_id: session.session_id },
        });
        void router.push(`/scribe/session/${session.session_id}`);
      }
    },
    [
      currentSessionId,
      isEhrActionPending,
      onLinkEhrPatientToSession,
      postIntegrationWorkflowSession,
      router,
    ]
  );

  // Generate unique key for each session
  const getSessionKey = useCallback((session: ConsolidatedSession, index: number) => {
    if (session.session_id) {
      return session.session_id;
    } else if (
      session.externalApptData &&
      isLegacyEhrAppointment(session.externalApptData)
    ) {
      return session.externalApptData.data.record_id;
    }
    return `${index}-${session.created_time_local}-${session.displayName}`;
  }, []);

  return (
    <div className="relative flex size-full w-full flex-col gap-2 sm:gap-1">
      <SessionListHeader />
      {/* Unified sessions list */}
      <div
        className={cn(
          'flex w-full flex-col transition-all ease-in-out',
          isSidebarSessionListOpen
            ? 'translate-y-0 opacity-100 duration-1000'
            : '-translate-y-5 opacity-0'
        )}
        style={{ height }}
      >
        <SessionCreationInProgressBanner />
        <div className="relative flex min-h-0 w-full flex-auto flex-col overflow-hidden">
          {showSessionsListLoading ? (
            <SessionsListLoading />
          ) : showSessionsListAssistantView ? (
            <SessionsListAssistantView />
          ) : showNoSessions ? (
            <div className="flex h-full flex-col items-center justify-center gap-4">
              <div className="content-center rounded-lg bg-background-tertiary p-4">
                <CalendarX size={23} className="text-text-secondary" />
              </div>
              <TypographyP2 className="text-text-secondary">
                <FormattedMessage {...consolidatedSessionsListMessages.noSessionsFound} />
              </TypographyP2>
            </div>
          ) : (
            <div
              className={cn(
                'scrollbar-fade flex h-full flex-col gap-0.5 overflow-y-auto',
                isMobile && 'pb-8'
              )}
            >
              {consolidatedSessions?.map((session, i) => (
                <ConsolidatedSessionListItem
                  key={getSessionKey(session, i)}
                  session={session}
                  index={i}
                  prevSession={i > 0 ? consolidatedSessions[i - 1] : undefined}
                  nextSession={
                    i < consolidatedSessions.length - 1
                      ? consolidatedSessions[i + 1]
                      : undefined
                  }
                  onSessionClick={handleSessionClick}
                />
              ))}
              {isEhrActionPending && <EhrSessionsListLoader />}
            </div>
          )}
          {showUploadProcessingOverlay && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface/80 backdrop-blur-sm">
              <div className="w-full max-w-[280px]">
                <SessionsListLoading />
              </div>
            </div>
          )}
        </div>
        {canCreateSessions && <UploadSessionScheduleModal />}
      </div>
      {showSidebarEvidenceAwareness ? (
        <SessionlessEvidenceAwarenessPopover variant="sidebar" />
      ) : null}
      <SessionSelectionBar />
    </div>
  );
});

ConsolidatedSessionsBar.displayName = 'ConsolidatedSessionsBar';
export default ConsolidatedSessionsBar;
